// Typed calls to this app's own /api, through the SDK's authenticated fetch (token in memory,
// refreshed once on 401). Query keys start with the account id so switching accounts never shows
// another account's cached data.

import { useApiFetch, useHostContext } from '@waforms/app-sdk/client';
import {
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import type {
  ApiKeyDto,
  ConversationFormsDto,
  CreatedApiKeyDto,
  FormDetailDto,
  FormSummaryDto,
  HostStatusDto,
  ImageDto,
  InboxDto,
  SendDto,
  StatsDto,
  SubmissionDetailDto,
  SubmissionDto,
  TemplateUseDto,
  VersionDto,
} from '../../shared/api-types.ts';
import type { ApiScope } from '../../shared/api-scopes.ts';
import type { FormDefinition } from '../../shared/definition.ts';
import type { Issue } from '../../shared/validate.ts';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly issues: Issue[] = [],
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export function useApi() {
  const apiFetch = useApiFetch();
  return useCallback(
    async <T>(path: string, init: RequestInit = {}): Promise<T> => {
      const headers = new Headers(init.headers);
      if (init.body !== undefined) headers.set('Content-Type', 'application/json');
      headers.set('Accept', 'application/json');
      const response = await apiFetch(path, { ...init, headers });
      if (response.status === 204) return undefined as T;
      const body = (await response.json().catch(() => null)) as {
        error?: { code?: string; message?: string; issues?: Issue[] };
      } | null;
      if (!response.ok) {
        throw new ApiError(
          response.status,
          body?.error?.code ?? `http_${response.status}`,
          body?.error?.message ?? `Request failed (${response.status})`,
          body?.error?.issues ?? [],
        );
      }
      return body as T;
    },
    [apiFetch],
  );
}

/** Downloads a file from the API (the token cannot go in a link, so fetch → blob → save). */
export function useDownload() {
  const apiFetch = useApiFetch();
  return useCallback(
    async (path: string, fallbackName: string) => {
      const response = await apiFetch(path);
      if (!response.ok) throw new ApiError(response.status, `http_${response.status}`, 'Download failed');
      const disposition = response.headers.get('Content-Disposition') ?? '';
      const name = decodeURIComponent(/filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName);
      const url = URL.createObjectURL(await response.blob());
      const link = document.createElement('a');
      link.href = url;
      link.download = name;
      document.body.append(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    [apiFetch],
  );
}

export const useAccountId = (): number | undefined => useHostContext().account?.id;

export const keys = {
  all: (account: number | undefined) => ['forms-app', account] as const,
  forms: (account: number | undefined, status: string) => ['forms-app', account, 'forms', status] as const,
  form: (account: number | undefined, id: string) => ['forms-app', account, 'form', id] as const,
  inboxes: (account: number | undefined) => ['forms-app', account, 'inboxes'] as const,
  hostStatus: (account: number | undefined) => ['forms-app', account, 'host-status'] as const,
  apiKeys: (account: number | undefined) => ['forms-app', account, 'api-keys'] as const,
};

export function useInboxes() {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: keys.inboxes(account),
    queryFn: async () => (await api<{ inboxes: InboxDto[] }>('inboxes')).inboxes,
    staleTime: 60_000,
  });
}

export function useForms(status: 'active' | 'archived') {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: keys.forms(account, status),
    queryFn: async () => (await api<{ forms: FormSummaryDto[] }>(`forms?status=${status}`)).forms,
  });
}

/** Whether an administrator has connected the CRM for background work (Chatwoot host). */
export function useHostStatus(enabled: boolean) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: keys.hostStatus(account),
    queryFn: () => api<HostStatusDto>('host/status'),
    enabled,
    staleTime: 60_000,
  });
}

export function useForm(id: string) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: keys.form(account, id),
    queryFn: async () => (await api<{ form: FormDetailDto }>(`forms/${id}`)).form,
    // Poll while a publish is queued or running.
    refetchInterval: (query) => {
      const state = query.state.data?.publish.state;
      return state === 'queued' || state === 'running' ? 2000 : false;
    },
  });
}

/** Mutations on one form: each returns the fresh form and puts it in the cache. */
export function useFormMutations(id: string) {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  const store = (form: FormDetailDto) => {
    client.setQueryData(keys.form(account, id), form);
    void client.invalidateQueries({ queryKey: ['forms-app', account, 'forms'] });
    return form;
  };
  const call = async (path: string, init: RequestInit) =>
    store((await api<{ form: FormDetailDto }>(path, init)).form);

  return {
    save: useMutation({
      mutationFn: (input: {
        revision: number;
        name?: string;
        categories?: string[];
        definition?: FormDefinition;
        settings?: { onePerContact?: boolean; privateNote?: boolean };
        sendDefaults?: { body?: string; cta?: string; header?: string | null; footer?: string | null };
      }) => call(`forms/${id}`, { method: 'PUT', body: JSON.stringify(input) }),
    }),
    publish: useMutation({ mutationFn: () => call(`forms/${id}/publish`, { method: 'POST' }) }),
    archive: useMutation({ mutationFn: () => call(`forms/${id}/archive`, { method: 'POST' }) }),
    refreshStatus: useMutation({ mutationFn: () => call(`forms/${id}/refresh-status`, { method: 'POST' }) }),
  };
}

export function useCreateForm() {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: async (input: { name: string; inboxId: number; categories: string[] }) =>
      (await api<{ form: FormDetailDto }>('forms', { method: 'POST', body: JSON.stringify(input) })).form,
    onSuccess: (form) => {
      client.setQueryData(keys.form(account, form.id), form);
      void client.invalidateQueries({ queryKey: ['forms-app', account, 'forms'] });
    },
  });
}

export function useDuplicateForm() {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api<{ form: FormDetailDto }>(`forms/${id}/duplicate`, { method: 'POST' })).form,
    onSuccess: () => client.invalidateQueries({ queryKey: ['forms-app', account, 'forms'] }),
  });
}

export function useArchiveForm() {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api<{ form: FormDetailDto }>(`forms/${id}/archive`, { method: 'POST' })).form,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.all(account) }),
  });
}

export function usePreviewLink() {
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) => api<{ url: string; expiresAt: string | null }>(`forms/${id}/preview`),
  });
}

export function useVersions(id: string) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: [...keys.form(account, id), 'versions'],
    queryFn: async () => (await api<{ versions: VersionDto[] }>(`forms/${id}/versions`)).versions,
  });
}

export function useTemplateUses(id: string, liveFlowId: string | null) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: [...keys.form(account, id), 'templates', liveFlowId],
    queryFn: async () => (await api<{ templates: TemplateUseDto[] }>(`forms/${id}/templates`)).templates,
    enabled: liveFlowId !== null,
    staleTime: 60_000,
  });
}

export function useAttributes(enabled: boolean) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: ['forms-app', account, 'attributes'],
    queryFn: async () =>
      (await api<{ attributes: { key: string; name: string; type: string }[] }>('attributes')).attributes,
    enabled,
    staleTime: 60_000,
  });
}

export function useLabels(enabled: boolean) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: ['forms-app', account, 'labels'],
    queryFn: async () => (await api<{ labels: { title: string; color: string }[] }>('labels')).labels,
    enabled,
    staleTime: 60_000,
  });
}

export function useImage(id: string | null | undefined) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: ['forms-app', account, 'image', id],
    queryFn: () => api<{ image: ImageDto; dataUrl: string }>(`images/${id}`),
    enabled: Boolean(id),
    staleTime: Infinity,
  });
}

export function useUploadImage() {
  const api = useApi();
  return useMutation({
    mutationFn: async (data: string) =>
      (await api<{ image: ImageDto }>('images', { method: 'POST', body: JSON.stringify({ data }) })).image,
  });
}

export interface SubmissionFilters {
  from?: string;
  to?: string;
  q?: string;
  duplicates?: 'include' | 'exclude';
}

export const timeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export function filterQuery(filters: SubmissionFilters): URLSearchParams {
  const params = new URLSearchParams({ tz: timeZone() });
  if (filters.from) params.set('from', filters.from);
  if (filters.to) params.set('to', filters.to);
  if (filters.q?.trim()) params.set('q', filters.q.trim());
  if (filters.duplicates) params.set('duplicates', filters.duplicates);
  return params;
}

export function useSubmissions(formId: string, filters: SubmissionFilters) {
  const api = useApi();
  const account = useAccountId();
  return useInfiniteQuery({
    queryKey: [...keys.form(account, formId), 'submissions', filters],
    queryFn: ({ pageParam }) => {
      const params = filterQuery(filters);
      params.set('limit', '25');
      if (pageParam) params.set('before', pageParam);
      return api<{ submissions: SubmissionDto[]; nextBefore: string | null }>(
        `forms/${formId}/submissions?${params.toString()}`,
      );
    },
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.nextBefore,
    placeholderData: keepPreviousData,
  });
}

export function useSubmission(formId: string, submissionId: string | null) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: [...keys.form(account, formId), 'submission', submissionId],
    queryFn: async () =>
      (await api<{ submission: SubmissionDetailDto }>(`forms/${formId}/submissions/${submissionId}`))
        .submission,
    enabled: submissionId !== null,
  });
}

export function useStats(formId: string, days: number) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: [...keys.form(account, formId), 'stats', days],
    queryFn: () => api<StatsDto>(`forms/${formId}/stats?days=${days}&tz=${encodeURIComponent(timeZone())}`),
  });
}

// ---- conversation tab ----

export function useConversationForms(conversationId: number | undefined) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: ['forms-app', account, 'conversation', conversationId, 'forms'],
    queryFn: () => api<ConversationFormsDto>(`conversations/${conversationId}/forms`),
    enabled: conversationId !== undefined,
  });
}

export function useContactActivity(contactId: number | undefined, conversationId: number | undefined) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: ['forms-app', account, 'contact', contactId, 'activity'],
    queryFn: () =>
      api<{ sends: SendDto[]; submissions: SubmissionDto[] }>(
        `contacts/${contactId}/activity${conversationId ? `?conversationId=${conversationId}` : ''}`,
      ),
    enabled: contactId !== undefined,
    refetchInterval: 20_000,
  });
}

export function useSendForm(conversationId: number | undefined, contactId: number | undefined) {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: async (input: {
      formId: string;
      body: string;
      cta: string;
      header?: string | null;
      footer?: string | null;
    }) =>
      (
        await api<{ send: SendDto }>(`conversations/${conversationId}/sends`, {
          method: 'POST',
          body: JSON.stringify(input),
        })
      ).send,
    onSuccess: () => client.invalidateQueries({ queryKey: ['forms-app', account, 'contact', contactId] }),
  });
}

// ---- public API keys (administrators) ----

export function useApiKeys(enabled: boolean) {
  const api = useApi();
  const account = useAccountId();
  return useQuery({
    queryKey: keys.apiKeys(account),
    queryFn: async () => (await api<{ apiKeys: ApiKeyDto[] }>('api-keys')).apiKeys,
    enabled,
  });
}

export function useCreateApiKey() {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: (input: { name: string; scopes: ApiScope[] }) =>
      api<CreatedApiKeyDto>('api-keys', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.apiKeys(account) }),
  });
}

export function useRevokeApiKey() {
  const api = useApi();
  const client = useQueryClient();
  const account = useAccountId();
  return useMutation({
    mutationFn: async (id: string) =>
      (await api<{ apiKey: ApiKeyDto }>(`api-keys/${id}`, { method: 'DELETE' })).apiKey,
    onSuccess: () => client.invalidateQueries({ queryKey: keys.apiKeys(account) }),
  });
}
